import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BobineSceau, SceauIndicator } from './BobineSceau';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const serif = "'Georgia', 'Times New Roman', serif";
const montserrat = "'Montserrat', 'Arial', sans-serif";

/* ── Mini Album Picker for Timeline ── */
const TimelineAlbumPicker = ({ photo, albums, setAlbums, albumPickerPhotoId, setAlbumPickerPhotoId, token }) => {
  if (albumPickerPhotoId !== photo.id) return null;

  return (
    <>
      <div onClick={(e) => { e.stopPropagation(); setAlbumPickerPhotoId(null); }}
        style={{ position: 'absolute', inset: 0, zIndex: 18, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 6 }} />
      <div data-testid={`timeline-album-picker-${photo.id}`} onClick={e => e.stopPropagation()} style={{
        position: 'absolute', bottom: 36, left: 2, right: 2, zIndex: 19,
        backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.25)', borderRadius: 6,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.6)', maxHeight: 160, overflowY: 'auto',
      }}>
        <div style={{ padding: '5px 8px', borderBottom: '1px solid rgba(212,175,55,0.08)' }}>
          <span style={{ fontFamily: serif, fontSize: 9, color: '#D4AF37' }}>Gérer les écrins</span>
        </div>
        {albums.map(a => {
          const alreadyIn = (a.photo_ids || []).includes(photo.id);
          return (
            <button key={a.id} data-testid={`timeline-picker-album-${a.id}`}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  if (alreadyIn) {
                    await fetch(`${API}/albums/${a.id}/remove-photos`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ photo_ids: [photo.id] }) });
                    setAlbums(prev => prev.map(al => al.id === a.id ? { ...al, photo_count: Math.max(0, (al.photo_count || 1) - 1), photo_ids: (al.photo_ids || []).filter(pid => pid !== photo.id) } : al));
                    toast(`Trésor retiré de ${a.name}`, { style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }, duration: 2500 });
                  } else {
                    await fetch(`${API}/albums/${a.id}/add-photos`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ photo_ids: [photo.id] }) });
                    setAlbums(prev => prev.map(al => al.id === a.id ? { ...al, photo_count: (al.photo_count || 0) + 1, photo_ids: [...(al.photo_ids || []), photo.id] } : al));
                    toast(`Trésor ajouté à ${a.name}`, { style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }, duration: 2500 });
                  }
                } catch { /* silent */ }
                setAlbumPickerPhotoId(null);
              }}
              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 5, fontFamily: serif, fontSize: 10, color: '#D4AF37', background: 'none', border: 'none', padding: '6px 8px', cursor: 'pointer', opacity: alreadyIn ? 1 : 0.7 }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
              {a.name}
              <span style={{ fontSize: 8, marginLeft: 'auto', fontWeight: 600 }}>{alreadyIn ? 'Retirer' : 'Ajouter'}</span>
            </button>
          );
        })}
      </div>
    </>
  );
};

/* ── Timeline Photo Card with Actions ── */
const TimelinePhotoCard = ({
  photo, globalIdx, onPhotoClick, token,
  toggleBook, setDeleteConfirm, familyCircle, familyRole, setFamilyCircle,
  albums, setAlbums, albumPickerPhotoId, setAlbumPickerPhotoId, pendingComments,
}) => {
  const [downloadConfirm, setDownloadConfirm] = useState(null);

  return (
    <div
      key={photo.id}
      data-testid={`timeline-photo-${photo.id}`}
      style={{
        position: 'relative', aspectRatio: '1',
        cursor: 'pointer',
      }}
    >
      {/* Image wrapper — clips the photo to rounded corners */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 6, overflow: 'hidden',
        border: photo.in_book ? '2px solid #D4AF37' : '1px solid rgba(212,175,55,0.1)',
        transition: 'border-color 0.2s',
      }}>
      {/* Image — click opens viewer */}
      <div onClick={() => onPhotoClick(globalIdx >= 0 ? globalIdx : 0)} style={{ width: '100%', height: '100%' }}>
        <img
          src={`${API}/photos/${photo.id}/thumbnail?token=${token}`}
          alt="" loading="lazy"
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transform: photo.rotation ? `rotate(${photo.rotation}deg)` : undefined,
          }}
        />
      </div>

      {/* Book badge */}
      {photo.in_book && (
        <div style={{ position: 'absolute', top: 4, left: 4, background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', fontFamily: serif, fontSize: 8, padding: '2px 5px', borderRadius: 3, fontWeight: 600 }}>
          Livre
        </div>
      )}

      {/* Audio badge */}
      {photo.audio_filename && (
        <SceauIndicator type="audio" style={{ position: 'absolute', top: 4, left: photo.in_book ? 50 : 4, transform: 'scale(0.8)', transformOrigin: 'top left' }} />
      )}

      {/* Comment badge */}
      {pendingComments?.some(c => c.status === 'approved' && c.photo_id === photo.id) && (
        <SceauIndicator type="comment" style={{ position: 'absolute', top: photo.audio_filename ? 22 : 4, left: photo.in_book ? 50 : 4, transform: 'scale(0.8)', transformOrigin: 'top left' }} />
      )}

      {/* Ecrin dot */}
      {albums?.some(a => (a.photo_ids || []).includes(photo.id)) && (
        <div style={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', boxShadow: '0 0 4px rgba(212,175,55,0.5)' }} />
      )}

      {/* Month badge */}
      {photo.month && (
        <span style={{
          position: 'absolute', bottom: 32, left: 3,
          fontFamily: serif, fontSize: 8, color: '#D4AF37',
          backgroundColor: 'rgba(0,0,0,0.7)', padding: '1px 4px',
          borderRadius: 2, letterSpacing: 0.5, zIndex: 5,
        }}>
          {['', 'Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'][photo.month] || ''}
        </span>
      )}

      </div>
      {/* Actions bar — compact for timeline */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, padding: '6px 2px 2px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.85) 40%)',
        zIndex: 10, borderRadius: '0 0 6px 6px',
      }}>
        {/* Memoire */}
        <button data-testid={`tl-book-${photo.id}`} onClick={(e) => { e.stopPropagation(); toggleBook(photo.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, opacity: photo.in_book ? 1 : 0.6, display: 'flex' }}>
          <BobineSceau size={11} />
        </button>
        {/* Sauver */}
        <button data-testid={`tl-download-${photo.id}`} onClick={(e) => { e.stopPropagation(); setDownloadConfirm(photo.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, opacity: 0.6, display: 'flex' }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        {/* Supprimer */}
        <button data-testid={`tl-delete-${photo.id}`} onClick={(e) => { e.stopPropagation(); setDeleteConfirm(photo.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, opacity: 0.6, display: 'flex' }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
          </svg>
        </button>
        {/* Cercle */}
        {familyCircle && familyRole === 'guardian' && (
          <button data-testid={`tl-circle-${photo.id}`}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const isShared = familyCircle.shared_photo_ids?.includes(photo.id);
                const endpoint = isShared ? 'unshare-photos' : 'share-photos';
                await fetch(`${API}/family/${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ photo_ids: [photo.id] }) });
                setFamilyCircle(prev => ({ ...prev, shared_photo_ids: isShared ? (prev.shared_photo_ids || []).filter(id => id !== photo.id) : [...(prev.shared_photo_ids || []), photo.id] }));
              } catch { /* silent */ }
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, opacity: familyCircle.shared_photo_ids?.includes(photo.id) ? 1 : 0.6, display: 'flex' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </button>
        )}
        {/* Ecrin */}
        {albums?.length > 0 && (
          <button data-testid={`tl-album-${photo.id}`}
            onClick={(e) => { e.stopPropagation(); setAlbumPickerPhotoId(albumPickerPhotoId === photo.id ? null : photo.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, opacity: 0.6, display: 'flex' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
              <line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/>
            </svg>
          </button>
        )}
      </div>

      {/* Album picker dropdown */}
      <TimelineAlbumPicker
        photo={photo} albums={albums} setAlbums={setAlbums}
        albumPickerPhotoId={albumPickerPhotoId} setAlbumPickerPhotoId={setAlbumPickerPhotoId}
        token={token}
      />

      {/* Download confirmation */}
      {downloadConfirm === photo.id && (
        <div data-testid={`tl-download-confirm-${photo.id}`} onClick={(e) => { e.stopPropagation(); setDownloadConfirm(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#111', borderRadius: 14, border: '1px solid rgba(212,175,55,0.3)', padding: '32px 28px', maxWidth: 360, width: '90%', textAlign: 'center' }}>
            <p style={{ fontFamily: serif, fontSize: 16, color: '#D4AF37', marginBottom: 8 }}>Sauvegarder ce Tresor ?</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20 }}>
              <button onClick={(e) => { e.stopPropagation(); setDownloadConfirm(null); }}
                style={{ fontFamily: serif, fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.4)' }}>Annuler</button>
              <button onClick={(e) => { e.stopPropagation(); const a = document.createElement('a'); a.href = `${API}/photos/${photo.id}/image?token=${token}`; a.download = 'tresor'; a.click(); setDownloadConfirm(null); }}
                style={{ fontFamily: serif, fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: 'pointer', color: '#000', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: 'none' }}>Telecharger</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main TimelineView ── */
const TimelineView = ({
  token, onPhotoClick, photos: existingPhotos,
  toggleBook, setDeleteConfirm, deleteConfirm, handleDelete,
  familyCircle, familyRole, setFamilyCircle,
  albums, setAlbums, albumPickerPhotoId, setAlbumPickerPhotoId,
  pendingComments, t,
}) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzed, setAnalyzed] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/photos/timeline`, { headers });
      setTimeline(res.data.timeline || []);
    } catch (e) {
      console.error('Timeline fetch error', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const init = async () => {
      if (!analyzed) {
        try {
          await axios.post(`${API}/photos/analyze-exif`, {}, { headers });
        } catch (e) { /* Non-blocking */ }
        setAnalyzed(true);
      }
      fetchTimeline();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', margin: '0 auto 12px',
          border: '2px solid rgba(212,175,55,0.15)', borderTopColor: '#D4AF37',
          animation: 'spin 1s linear infinite',
        }} />
        <p style={{ fontFamily: serif, fontSize: 13, color: '#D4AF37' }}>Organisation chronologique...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0' }}>Aucun souvenir dans le coffre.</p>
      </div>
    );
  }

  let lastDecade = null;

  return (
    <div data-testid="timeline-view" style={{ position: 'relative', paddingLeft: 28 }}>
      <div style={{
        position: 'absolute', left: 8, top: 0, bottom: 0, width: 1,
        background: 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.3) 5%, rgba(212,175,55,0.3) 95%, transparent)',
      }} />

      {timeline.map((yearGroup) => {
        const year = yearGroup.year;
        const decade = yearGroup.decade;
        const showDecade = decade && decade !== lastDecade;
        if (decade) lastDecade = decade;

        return (
          <div key={year} style={{ marginBottom: 32 }}>
            {showDecade && (
              <div data-testid={`decade-${decade}`} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                marginBottom: 20, marginLeft: -28, paddingLeft: 0,
              }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  border: '2px solid #D4AF37', backgroundColor: '#000',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'linear-gradient(145deg, #D4AF37, #B8860B)' }} />
                </div>
                <span style={{ fontFamily: montserrat, fontSize: 11, fontWeight: 700, color: '#D4AF37', letterSpacing: 3, textTransform: 'uppercase', opacity: 0.6 }}>
                  Annees {decade}
                </span>
                <div style={{ flex: 1, height: 1, backgroundColor: 'rgba(212,175,55,0.12)' }} />
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, marginLeft: -24 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', flexShrink: 0, boxShadow: '0 0 10px rgba(212,175,55,0.4)' }} />
              <h3 data-testid={`year-${year}`} style={{ fontFamily: serif, fontSize: 20, color: '#D4AF37', fontWeight: 400, letterSpacing: 1, margin: 0 }}>
                {year > 0 ? year : 'Non date'}
              </h3>
              <span style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0' }}>
                {yearGroup.photos.length} souvenir{yearGroup.photos.length > 1 ? 's' : ''}
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: 8,
            }}>
              {yearGroup.photos.map((photo) => {
                const globalIdx = existingPhotos?.findIndex(p => p.id === photo.id);
                return (
                  <TimelinePhotoCard
                    key={photo.id}
                    photo={photo} globalIdx={globalIdx}
                    onPhotoClick={onPhotoClick} token={token}
                    toggleBook={toggleBook} setDeleteConfirm={setDeleteConfirm}
                    familyCircle={familyCircle} familyRole={familyRole} setFamilyCircle={setFamilyCircle}
                    albums={albums} setAlbums={setAlbums}
                    albumPickerPhotoId={albumPickerPhotoId} setAlbumPickerPhotoId={setAlbumPickerPhotoId}
                    pendingComments={pendingComments}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div data-testid="tl-delete-confirm-modal" onClick={() => setDeleteConfirm(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#111', borderRadius: 14, border: '1px solid rgba(212,175,55,0.3)', padding: '32px 28px', maxWidth: 360, width: '90%', textAlign: 'center' }}>
            <p style={{ fontFamily: serif, fontSize: 16, color: '#D4AF37', marginBottom: 8 }}>
              Voulez-vous vraiment supprimer ce tresor ?
            </p>
            <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', marginBottom: 24, lineHeight: 1.6 }}>
              Ce souvenir disparaitra definitivement de votre Coffre-fort. Cette action est irreversible.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ fontFamily: serif, fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.4)' }}>
                {t?.('cancel') || 'Annuler'}
              </button>
              <button onClick={() => handleDelete(deleteConfirm)}
                style={{ fontFamily: serif, fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: 'pointer', color: '#000', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: 'none', fontWeight: 600 }}>
                {t?.('dash_delete') || 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineView;
